import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from '@mui/material';
import { AttachMoney } from '@mui/icons-material';
import { InvoicePaymentMethod } from '../../../interfaces/common/merchant-invoices';

interface PaymentMethodSelectorProps {
  value: InvoicePaymentMethod | null;
  onChange: (method: InvoicePaymentMethod) => void;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  showLabel?: boolean;
  centered?: boolean;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Payment Method',
  helperText,
  showLabel = true,
  centered = false,
}) => {
  return (
    <FormControl component="fieldset" disabled={disabled}>
      {showLabel && (
        <Typography 
          variant="subtitle2" 
          fontWeight="bold" 
          sx={{ mb: 1, textAlign: centered ? 'center' : 'inherit' }}
        >
          {label}
        </Typography>
      )}
      <RadioGroup
        value={value || ''}
        onChange={(e) => onChange(e.target.value as InvoicePaymentMethod)}
        row
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row', 
          gap: 1.5, 
          flexWrap: 'wrap',
          justifyContent: centered ? 'center' : 'flex-start'
        }}>
          {/* Cash Option */}
          <FormControlLabel
            value={InvoicePaymentMethod.CASH}
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney sx={{ color: '#1976d2' }} />
                <Typography>Cash</Typography>
              </Box>
            }
            sx={{
              border: '1px solid',
              borderColor: value === InvoicePaymentMethod.CASH ? '#1976d2' : 'rgba(0,0,0,0.12)',
              borderRadius: 1,
              padding: '8px 12px',
              margin: 0,
              minWidth: 'fit-content',
              backgroundColor: value === InvoicePaymentMethod.CASH ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: value === InvoicePaymentMethod.CASH ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0,0,0,0.04)',
                borderColor: value === InvoicePaymentMethod.CASH ? '#1976d2' : 'rgba(0,0,0,0.23)',
              },
            }}
          />

          {/* Whish Money Option */}
          <FormControlLabel
            value={InvoicePaymentMethod.WHISH}
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img 
                  src="/whishmoneyicon.svg" 
                  alt="Whish Money"
                  style={{ 
                    height: '24px',
                    objectFit: 'contain',
                  }} 
                />
              </Box>
            }
            sx={{
              border: '1px solid',
              borderColor: value === InvoicePaymentMethod.WHISH ? '#d32f2f' : 'rgba(0,0,0,0.12)',
              borderRadius: 1,
              padding: '8px 12px',
              margin: 0,
              minWidth: 'fit-content',
              backgroundColor: value === InvoicePaymentMethod.WHISH ? 'rgba(211, 47, 47, 0.08)' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: value === InvoicePaymentMethod.WHISH ? 'rgba(211, 47, 47, 0.12)' : 'rgba(0,0,0,0.04)',
                borderColor: value === InvoicePaymentMethod.WHISH ? '#d32f2f' : 'rgba(0,0,0,0.23)',
              },
            }}
          />
        </Box>
      </RadioGroup>
      {helperText && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            mt: 1, 
            display: 'block',
            textAlign: centered ? 'center' : 'inherit'
          }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default PaymentMethodSelector;

